<template>
    <div
        class="flex items-center justify-between"
        data-spec-class="pagination"
    >
        <div class="flex flex-1 justify-between sm:hidden">
            <BasePaginationLink
                :theme="theme"
                :type="isFirstPage ? 'disabled' : 'normal'"
                class="!w-auto"
                data-spec-class="pagination__previous-m"
                @click="onPreviousPageClick"
            >
                {{ $t('baseComponents.basePagination.previous') }}
            </BasePaginationLink>

            <div v-if="hasMetaInfo">
                <PageSizeSelector
                    :theme="theme"
                    class="!w-auto"
                    :page-sizes="pageSizes"
                    :page-size="meta.per_page"
                    @change="$emit('update:page-size', $event)"
                />
            </div>

            <BasePaginationLink
                :theme="theme"
                :type="isLastPage ? 'disabled' : 'normal'"
                class="!w-auto"
                data-spec-class="pagination__next-m"
                @click="onNextPageClick"
            >
                {{ $t('baseComponents.basePagination.next') }}
            </BasePaginationLink>
        </div>

        <div class="hidden sm:grid sm:flex-1 sm:grid-cols-3 sm:items-center sm:gap-3">
            <div class="flex justify-start">
                <!-- eslint-disable vue/no-v-html -->
                <p
                    v-if="hasMetaInfo"
                    class="text-sm text-tertiary"
                    data-spec-class="pagination__meta-info"
                    v-html="
                        $t('baseComponents.basePagination.metaInfo', {
                            itemsFrom: meta.from,
                            itemsTo: meta.to,
                            itemsTotal: meta.total,
                        })
                    "
                />
            </div>
            <!-- eslint-enable vue/no-v-html -->
            <div class="flex justify-center">
                <PageSizeSelector
                    v-if="hasMetaInfo"
                    :theme="theme"
                    :page-sizes="pageSizes"
                    :page-size="meta.per_page"
                    @change="$emit('update:page-size', $event)"
                />
            </div>
            <div class="flex justify-end">
                <nav
                    class="inline-flex gap-2"
                    aria-label="Pagination"
                >
                    <BasePaginationLink
                        :theme="theme"
                        :type="isFirstPage ? 'disabled' : 'normal'"
                        data-spec-class="pagination__previous-d"
                        @click="onPreviousPageClick"
                    >
                        <span class="sr-only">
                            {{ $t('baseComponents.basePagination.previous') }}
                        </span>
                        <ChevronLeftIcon
                            class="h-5 w-5"
                            aria-hidden="true"
                        />
                    </BasePaginationLink>

                    <template v-if="meta.last_page > 7">
                        <BasePaginationLink
                            :theme="theme"
                            :type="isFirstPage ? 'current' : 'normal'"
                            data-spec-id="pagination__position-1"
                            data-spec-class="pagination__position"
                            @click="isFirstPage ? () => {} : $emit('update:page', 1)"
                        >
                            {{ 1 }}
                        </BasePaginationLink>

                        <BasePaginationLink
                            v-for="linkObject in clippingLinkObjects"
                            :key="linkObject.position"
                            :theme="theme"
                            :type="linkObject.type"
                            :data-spec-id="`pagination__position-${linkObject.position}`"
                            data-spec-class="pagination__position"
                            @click="linkObject.click"
                        >
                            {{ linkObject.label }}
                        </BasePaginationLink>

                        <BasePaginationLink
                            :theme="theme"
                            :type="isLastPage ? 'current' : 'normal'"
                            data-spec-id="pagination__position-7"
                            data-spec-class="pagination__position"
                            @click="isLastPage ? () => {} : $emit('update:page', meta.last_page)"
                        >
                            {{ meta.last_page }}
                        </BasePaginationLink>
                    </template>

                    <template v-else>
                        <BasePaginationLink
                            v-for="page in meta.last_page"
                            :key="page"
                            :theme="theme"
                            :type="page === meta.current_page ? 'current' : 'normal'"
                            :data-spec-id="`pagination__position-${page}`"
                            data-spec-class="pagination__position"
                            @click="
                                page === meta.current_page ? () => {} : $emit('update:page', page)
                            "
                        >
                            {{ page }}
                        </BasePaginationLink>
                    </template>

                    <BasePaginationLink
                        :theme="theme"
                        :type="isLastPage ? 'disabled' : 'normal'"
                        data-spec-class="pagination__next-d"
                        @click="onNextPageClick"
                    >
                        <span class="sr-only">
                            {{ $t('baseComponents.basePagination.next') }}
                        </span>
                        <ChevronRightIcon
                            class="h-5 w-5"
                            aria-hidden="true"
                        />
                    </BasePaginationLink>
                </nav>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import { PaginationMeta, Theme } from '@/types';
import PageSizeSelector from './BasePagination/PageSizeSelector.vue';
import BasePaginationLink from './BasePaginationLink.vue';

interface Props {
    meta: PaginationMeta;
    theme?: Theme;
    hasMetaInfo?: boolean;
    pageSizes?: number[];
}

const props = withDefaults(defineProps<Props>(), {
    theme: Theme.Light,
    hasMetaInfo: true,
    pageSizes: () => [15, 30, 50, 100],
});

const emits = defineEmits(['update:page', 'update:page-size']);

const isFirstPage = computed(() => props.meta.current_page === 1);

const isLastPage = computed(() => props.meta.current_page === props.meta.last_page);

function onPreviousPageClick() {
    if (isFirstPage.value) {
        return () => ({});
    }
    return emits('update:page', props.meta.current_page - 1);
}

function onNextPageClick() {
    if (isLastPage.value) {
        return () => ({});
    }
    return emits('update:page', props.meta.current_page + 1);
}

function clippingLinkObjectByPosition(position) {
    const positionCount = 7;
    const clippingThreshold = 3;
    const positionOffset = position + clippingThreshold - positionCount;
    let isClippingRight = false;
    let isClippingLeft = false;
    let positionPage;
    let positionType;

    // is clipping right
    if (props.meta.current_page <= clippingThreshold) {
        isClippingRight = true;
        positionPage = position;
        if (position === 6) {
            positionType = 'disabled';
        }
    }

    // is clipping left
    if (props.meta.current_page >= props.meta.last_page - clippingThreshold) {
        isClippingLeft = true;
        positionPage = props.meta.last_page - (positionCount - position);
        if (position === 2) {
            positionType = 'disabled';
        }
    }

    // is clipping left and right
    if (!isClippingLeft && !isClippingRight) {
        positionPage = props.meta.current_page + positionOffset;
        if (position === 2 || position === 6) {
            positionType = 'disabled';
        }
    }

    positionType =
        positionType || (positionPage === props.meta.current_page ? 'current' : 'normal');
    return {
        position,
        label: positionType === 'disabled' ? '...' : positionPage,
        type: positionType,
        click: positionType === 'normal' ? () => emits('update:page', positionPage) : () => ({}),
    };
}

const clippingLinkObjects = computed(() =>
    [2, 3, 4, 5, 6].map((position) => clippingLinkObjectByPosition(position))
);
</script>
